// FabricationOrderAcceptanceList — MOD fabrication order list (S16, S17): a Paper panel with a
// clickable-card list, each card carrying its acceptance-status Chip badge. Styled with MIC theme
// tokens (no hardcoded colors) so it reads as a regular MIC screen and follows the app's light/dark
// theme. Selecting a fabrication order opens its acceptance form (handled by the container). The
// selection rule (RN7) is enforced in Sun and reaches this component through `selectable` —
// non-selectable fabrication orders (NOT_AVAILABLE) render as a disabled, non-clickable card.
import { Box, Chip, Paper, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import {
  FABRICATION_ORDER_ACCEPTANCE_STATUSES, getStatusLabelKey, getStatusChipProps,
  getResultLabelKey, getResultChipProps
} from './fabricationOrderAcceptanceStatus'

/**
 * Props:
 *   fabricationOrders — array of { fabricationOrderId, identification, status, selectable }.
 *   selectedId        — fabricationOrderId of the currently selected fabrication order (controlled).
 *   onSelect          — (fabricationOrder) => void, called when a selectable card is clicked.
 */
export default function FabricationOrderAcceptanceList ({ fabricationOrders = [], selectedId, onSelect }) {
  const { t_ } = useSmartTranslation()

  return (
    <Paper
      data-testid='fabrication-order-acceptance-list'
      elevation={1}
      sx={{
        p: 1.5,
        width: 300,
        flexShrink: 0
      }}
    >
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
        {t_('Fabrication orders')}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.25 }}>
        {t_('Select a fabrication order to fill the acceptance form')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {fabricationOrders.map((fabricationOrder) => {
          // Openable = has an acceptance record (reached the step): PENDING / IN_PROGRESS / FINISHED.
          // Only NOT_AVAILABLE stays non-openable. FINISHED opens to view the closed version (and start
          // the next one if it failed) (#70).
          const openable = fabricationOrder.status !== FABRICATION_ORDER_ACCEPTANCE_STATUSES.NOT_AVAILABLE
          const isActive = selectedId === fabricationOrder.fabricationOrderId
          return (
            <Box
              key={fabricationOrder.fabricationOrderId}
              data-testid={`fabrication-order-card-${fabricationOrder.fabricationOrderId}`}
              role='button'
              aria-label={`${t_('Select')} ${fabricationOrder.identification}`}
              aria-disabled={!openable}
              aria-pressed={isActive}
              tabIndex={openable ? 0 : -1}
              onClick={openable && onSelect ? () => onSelect(fabricationOrder) : undefined}
              sx={{
                border: 1,
                borderColor: isActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 1.25,
                backgroundColor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'action.hover',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                cursor: openable ? 'pointer' : 'not-allowed',
                opacity: openable ? 1 : 0.55,
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': openable ? { borderColor: 'primary.main' } : {}
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                  {fabricationOrder.identification}
                </Typography>
                {/* Version number (versionNumber) of the active/last version (B4). Hidden until the
                    fabrication order has reached the circuit (no version yet). */}
                {fabricationOrder.versionNumber
                  ? (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      data-testid={`fabrication-order-version-${fabricationOrder.fabricationOrderId}`}
                    >
                      {`${t_('Version')} ${fabricationOrder.versionNumber}`}
                    </Typography>
                    )
                  : undefined}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                <Chip
                  size='small'
                  data-testid={`fabrication-order-status-badge-${fabricationOrder.fabricationOrderId}`}
                  label={t_(getStatusLabelKey(fabricationOrder.status))}
                  {...getStatusChipProps(fabricationOrder.status)}
                />
                {/* Result (PASSED/FAILED) of the last closed version, empty if none closed yet (B4). */}
                {getResultLabelKey(fabricationOrder.result) && (
                  <Chip
                    size='small'
                    data-testid={`fabrication-order-result-${fabricationOrder.fabricationOrderId}`}
                    label={t_(getResultLabelKey(fabricationOrder.result))}
                    {...getResultChipProps(fabricationOrder.result)}
                  />
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}
